import type { EquipmentManual } from '@/types';

// 阳极振压成型车间设备操作手册库 v1
// 覆盖 9 台设备：糊料缸 / 称量斗 / 振压机 / 冷却台 / 模具加热站 / 真空泵 / 配料柜 / 糊料温控屏 / 班组任务台
export const anodeManuals: Record<string, EquipmentManual> = {

  'PASTE-101': {
    overview:
      '糊料保温缸（5 t 容量），把混捏好的高温糊料维持在 145~155°C 待用。' +
      '糊料是干料（煅后焦+粉料）和粘结剂（沥青）按 84:16 在 165°C 下混捏 30 分钟得到的可塑性物料。' +
      '保温缸内有 4 叶搅拌器（12 rpm 连续慢搅）+ 导热油加热夹套，是振压机的"上料源头"。',
    operatingProcedure: [
      '每小时查看糊料温度，偏离 ±5°C 调节加热夹套油温',
      '每班抽样检测糊料粘度（应 8000~12000 mPa·s）',
      '料位低于 30% 时通知混捏车间补料',
      '搅拌器持续运转，不得长时间停搅（结块）',
      '每周排放夹套底部冷凝水',
      '每月检查保温层完整性（漏温）',
    ],
    safetyNotes: [
      '罐顶 160°C + 高温糊料带烫伤风险，操作戴隔热手套',
      '沥青蒸气有毒，戴防毒面具，车间排风必须开启',
      '搅拌器停机超过 30 分钟必须放料，否则结成铁砣',
      '清缸作业必须断电锁卡 + 双人监护',
    ],
    troubleshooting: [
      { symptom: '糊料温度 < 135°C', action: '加热站油温提到 165°C，关闭夹套冷却旁通' },
      { symptom: '糊料温度 > 165°C', action: '降低油温到 155°C，检查温控阀粘连' },
      { symptom: '搅拌器电流 > 额定 1.5 倍', action: '糊料结块，立即降温到 145°C 软化' },
      { symptom: '料位下降异常快', action: '检查出料阀有无内漏 / 振压机消耗是否异常' },
      { symptom: '粘度跑偏（粘度仪显示异常）', action: '送实验室复测 + 检查配料柜的沥青配比' },
    ],
  },

  'WGT-401': {
    overview:
      '称量给料斗，每次振压前从糊料缸接 1080 kg 糊料（一块阳极的量），通过失重秤精确称量后下料给振压机。' +
      '斗容 1.5 t，下料口装电磁振动给料机控制流量。重量精度 ±0.5% 是关键质量保证。',
    operatingProcedure: [
      '每班开机前用 100 kg 标定块校准称量传感器',
      '每次下料前确认重量目标值（默认 1080 kg）',
      '下料过程中观察料流均匀性，不可有断流',
      '每周清理斗壁结垢糊料（防偏壁挂料）',
      '电磁振动器电流 5~8 A 为正常',
    ],
    safetyNotes: [
      '称量传感器是精密器件，禁止敲击或重物碰撞',
      '清斗内壁作业需断电 + 戴防护面罩',
      '严禁在称量过程中调整斗体（数据失真）',
    ],
    troubleshooting: [
      { symptom: '重量偏差 > ±2%', action: '校准传感器；检查斗体是否有杂物或挂料' },
      { symptom: '下料口堵塞', action: '关振动器，用专用钎子清通，重启升频率' },
      { symptom: '称量传感器零点漂移', action: '紧固称重传感器螺栓 + 标定' },
      { symptom: '出料断流', action: '检查电磁振动器是否过载跳闸' },
    ],
  },

  'FORM-201': {
    overview:
      '1250 t 振压成型机，是阳极生产的核心设备。糊料下到模具后，' +
      '通过液压压头 1250~1300 t 振压 + 50 Hz 高频振动 90~110 秒，' +
      '配合模具加热（130~145°C）和真空抽气（≤ 5 kPa）成型生阳极坯块。' +
      '日产 80~120 块，单块尺寸 1450 × 660 × 600 mm，重 1080 kg。',
    operatingProcedure: [
      '开机前预热模具到 135°C，启动真空泵抽至 ≤ 5 kPa',
      '设定振压压力 1280 t、振压时间 95 s、振动频率 50 Hz',
      '糊料下到模具后，关闭模具盖，确认密封',
      '启动振压，观察压力曲线和密度反馈',
      '振压结束自动抬压头 → 顶出生阳极 → 喷脱模剂',
      '每 50 块巡检模具表面有无划痕 / 粘料',
      '每班记录密度、合格率上报',
    ],
    safetyNotes: [
      '1250 t 巨大压力，振压期间严禁伸手入模',
      '液压油 60°C 高温 + 油雾，禁明火，戴防溅护目镜',
      '真空腔失压瞬间有反冲风险，操作必须在主控台远端',
      '模具温度 145°C，更换工装必须冷却到 60°C 以下',
    ],
    troubleshooting: [
      { symptom: '生阳极表面裂纹', action: '糊料温度提到 150°C + 模具预热到 135°C' },
      { symptom: '阳极顶部起鼓', action: '检查真空泵，绝压打到 ≤ 3 kPa + 延振压时间到 110 s' },
      { symptom: '阳极缺角', action: '加热功率提到 70 kW + 喷涂脱模剂均匀' },
      { symptom: '生阳极密度 < 1.55 g/cm³', action: '振压时间提到 100 s + 压力提到 1300 t + 频率到 50 Hz' },
      { symptom: '脱模困难（粘模）', action: '模具温度降到 140°C + 脱模剂用量 ×1.5' },
      { symptom: '液压压力波动 > ±50 t', action: '检查液压系统泄漏 / 蓄能器压力' },
    ],
  },

  'COOL-301': {
    overview:
      '生阳极冷却辊道，长 8 m 含 18 对辊筒，传送速度 0.03~0.08 m/s。' +
      '坯块从振压机出来时 220°C，沿辊道缓慢前进 + 自然风冷，到末端时降到 60°C，' +
      '便于后续叉车搬运到焙烧炉装炉位。冷却太快易产生应力裂纹，太慢影响产能。',
    operatingProcedure: [
      '开机前检查所有辊筒润滑，无卡死',
      '正常生产时传送速度 0.05 m/s（10 分钟过完整段）',
      '每 4 小时记录入口/出口温度，确保出口 ≤ 80°C',
      '辊道末端有满料光电传感器，触发时通知叉车',
      '每周清理辊筒间糊料粉末（防卡顿）',
    ],
    safetyNotes: [
      '入口 220°C 高温坯块，不可徒手接触',
      '辊筒转动夹手风险，禁止靠近运行中的辊缝',
      '坯块滑落征兆：传感器红灯亮，立即停机',
    ],
    troubleshooting: [
      { symptom: '出口温度 > 100°C', action: '降低传送速度到 0.03 m/s + 开侧排风扇' },
      { symptom: '某辊筒不转', action: '检查电机或链条 / 卡入糊料粉' },
      { symptom: '坯块碰撞', action: '检查辊筒高度，调平 + 适当增大间距' },
      { symptom: '末端排队过长（> 12 块）', action: '通知下游焙烧装炉，必要时调低振压机频次' },
    ],
  },

  'MHT-501': {
    overview:
      '模具加热站，通过 65 kW 电加热把导热油加热到 145°C，再泵送到振压机模具夹套。' +
      '油循环流量 8 m³/h，模具温度稳定在 130~145°C 是抗缺角/裂纹的关键。' +
      '采用 PID 闭环控制，从冷态升温到 135°C 约需 35 分钟。',
    operatingProcedure: [
      '冷开机前 40 分钟启动加热站预热',
      '设定目标温度 135°C，加热功率上限 80 kW',
      '每班巡检油位和油色（油色变深表示老化）',
      '每月清理油过滤器',
      '每年更换导热油（按厂家规范）',
    ],
    safetyNotes: [
      '导热油闪点 240°C，禁明火和高温焊接作业',
      '油压系统泄漏可能喷油烫伤，作业戴护目镜',
      '电加热元件高压，检修必断电锁卡',
    ],
    troubleshooting: [
      { symptom: '模具温度上不去（功率拉满仍 < 130°C）', action: '检查加热元件 / 油泵流量' },
      { symptom: '加热功率上不去', action: '检查电气开关 / SCR 模块' },
      { symptom: '油温波动大', action: '检查 PID 参数 / 温度传感器接触' },
      { symptom: '油位下降', action: '排查管路渗漏 + 补油' },
    ],
  },

  'VAC-601': {
    overview:
      '真空泵机组（罗茨+水环串联），抽气能力 200 m³/h，把振压机模具腔抽至 ≤ 5 kPa。' +
      '真空度是排气彻底度的关键，决定了生阳极有无气孔/起鼓。' +
      '正常生产时 3~5 kPa，振压保压期开抽以排气体。',
    operatingProcedure: [
      '开机前确认水环泵供水充足（水压 ≥ 0.2 MPa）',
      '先启罗茨泵预抽 30 秒，再启水环泵',
      '每班记录绝压和抽气流量',
      '每周清理真空过滤器粉尘',
      '每月检查泵油位 + 油色',
    ],
    safetyNotes: [
      '真空管路负压，禁止用手堵抽气口（吸伤）',
      '泵机温度 70°C，不可直接接触',
      '电机 380V 三相，断电必须双重验电',
    ],
    troubleshooting: [
      { symptom: '绝压 > 5 kPa', action: '检查管路法兰泄漏 / 真空阀密封' },
      { symptom: '抽气流量 < 150 m³/h', action: '清理过滤器 + 检查叶轮积粉' },
      { symptom: '泵噪声异常', action: '检查叶轮间隙 / 轴承' },
      { symptom: '水环泵冒水', action: '调节供水压力 + 排放底部废水' },
    ],
  },

  'CFG-701': {
    overview:
      '配料控制柜，控制干料配方比例（粗焦/中焦/细粉/沥青粉）。' +
      '阳极配方核心：石油焦粒度 28~32% + 细粒 33~37% + 沥青 14~16 wt%。' +
      '配方变化通过 PLC 下达到混捏车间，2 小时后才在糊料缸看到效果（库存周转）。',
    operatingProcedure: [
      '配方调整必须经车间主任审批',
      '调整后填《配方变更记录》，标注调整时间和原因',
      '每周校准料仓秤量传感器',
      '观察 4 小时后的密度/抗折强度反馈',
    ],
    safetyNotes: [
      '柜内 380V 电气，禁止带电作业',
      '配方变化大于 ±2% 必须通知化验室加密复测',
    ],
    troubleshooting: [
      { symptom: '密度持续偏低', action: '增加粗焦比例 + 微调沥青到 15.5%' },
      { symptom: '抗折强度低', action: '降低细粒比例（< 35%）' },
      { symptom: '糊料结块', action: '提高沥青到 15.5~16% 改善流动性' },
      { symptom: '料仓信号不响应', action: '排查 PLC 通讯 / IO 模块' },
    ],
  },

  'HMI-801': {
    overview:
      '糊料温控监控屏，实时显示糊料温度趋势 + 温度报警阈值设置 + 加热站状态。' +
      '是糊料温度链路的总控显示界面，温控老师傅每小时查看一次。',
    operatingProcedure: [
      '每班交接确认温度报警阈值（默认 165°C）',
      '温度跑偏报警时第一时间通知主控调整',
      '每月备份历史曲线数据',
    ],
    safetyNotes: ['屏幕清洁用干布，禁用酒精'],
    troubleshooting: [
      { symptom: '屏幕黑屏', action: '重启 HMI；检查 24V 电源' },
      { symptom: '温度数据冻结', action: '检查与 PLC 的 Modbus 通讯' },
    ],
  },

  'HMI-802': {
    overview:
      '班组任务台大屏，显示当班指标 / 已完成 / 一次合格率 / 故障告警。' +
      '是班组管理的核心抓手，班长每 2 小时更新一次进度。',
    operatingProcedure: [
      '交班时确认当班指标（默认 60 块）',
      '每完成 10 块在系统打卡更新',
      '合格率低于 92% 必须召开 QC 会议',
    ],
    safetyNotes: ['大屏支架紧固，防地震震落'],
    troubleshooting: [
      { symptom: '指标未更新', action: '检查与振压机的产量计数器同步' },
      { symptom: '合格率数据异常', action: '联系质检员现场复测' },
    ],
  },
};
